import React, { useEffect, useState } from "react";
import { Row, Col, Card, Spinner, Alert, Table, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import XGolifeDashboardComponent from "./components/XGolifeDashboardComponent";
import { FaUsers, FaBuilding, FaInfoCircle } from "react-icons/fa";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { API_URL } from "./config";
import { Link } from 'react-router-dom';

ChartJS.register(ArcElement, Tooltip, Legend);

const XGOLifeDashboard = () => {
  const [companyData, setCompanyData] = useState(null);
  const [branchData, setBranchData] = useState([]);
  const [userData, setUserData] = useState([]);
  const [productData, setProductData] = useState([]); // New state for product data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get company_id and branch_id from local storage
  const companyId = localStorage.getItem('company_id');
  const branchId = localStorage.getItem('branch_id');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true); // Set loading only for main content
        console.log("Fetching data for Company ID:", companyId);
        console.log("Fetching data for Branch ID:", branchId);

        const [companyRes, branchRes, usersRes, productRes] = await Promise.all([
          fetch(`${API_URL}/companysetup/${companyId}`),
          fetch(`${API_URL}/branch/company/${companyId}`),
          fetch(`${API_URL}/users/GetUsersByCompany/${companyId}`),
          fetch(`${API_URL}/shift/shiftdistributionbycompanyidbranchid/${companyId}/${branchId}`)
        ]);

        const company = await companyRes.json();
        const branches = await branchRes.json();
        const users = await usersRes.json();
        const products = await productRes.json(); // Get product data

        console.log("Company Data:", company);
        console.log("Branches Data:", branches);
        console.log("Users Data:", users);
        console.log("Products Data:", products); // Log the fetched product data

        setCompanyData(company);
        setBranchData(Array.isArray(branches) ? branches : [branches]);
        setUserData(Array.isArray(users) ? users : [users]);
        setProductData(products); // Set product data
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to fetch data");
      } finally {
        setLoading(false); // Set loading to false after fetching data
      }
    };

    fetchData();
  }, [companyId, branchId]);

  // Prepare data for the Pie chart based on total_quantity
  const pieData = {
    labels: productData.map(product => product.product_name),
    datasets: [{
      data: productData.map(product => product.total_quantity),
      backgroundColor: ['#FFD700', '#FFA500', '#FF8C00', '#FF6347', '#4682B4'], // Adjust colors as needed
      borderColor: ['#fff', '#fff', '#fff', '#fff', '#fff'],
      borderWidth: 1,
    }],
  };

  return (
    <XGolifeDashboardComponent>
      <div style={{ backgroundColor: "#fff", minHeight: "100vh", padding: "20px" }}>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
            <Spinner animation="border" variant="warning" />
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Row className="mb-4">
              <Col>
                <h1 style={{ color: "#000" }}>
                  {companyData?.name} Dashboard
                </h1>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={4}>
                <Card className="shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-muted">Total Branches</h6>
                        <h2 style={{ color: "#B8860B" }}>{branchData.length}</h2>
                      </div>
                      <FaBuilding size={20} color="#FFD700" />
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={4}>
                <Card className="shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-muted">Total Users</h6>
                        <h2 style={{ color: "#B8860B" }}>{userData.length}</h2>
                      </div>
                      <FaUsers size={20} color="#FFD700" />
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={4}>
                <Card className="shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-muted">Company Size</h6>
                        <h2 style={{ color: "#B8860B" }}>{companyData?.company_size}</h2>
                      </div>
                      <FaInfoCircle size={20} color="#FFD700" />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row style={{ backgroundColor: 'white', padding: '10px', alignContent: 'start' }}>
              <Link to="/CloseOpenShift" style={{ textDecoration: 'none' }}>
                <Button style={{ width: '150px', backgroundColor: '#ffc000', border: 'none', alignSelf: 'start' }}>Close Shift</Button>
              </Link>
            </Row>

            <Row className="mb-4">
              <Col md={8}>
                <Card className="shadow-sm">
                  <Card.Header style={{ backgroundColor: "#FFD700", color: "#000" }}>
                    Branch Information
                  </Card.Header>
                  <Card.Body>
                    <div className="table-responsive">
                      <Table striped hover>
                        <thead>
                          <tr>
                            <th>Branch Name</th>
                            <th>Location</th>
                            <th>Type</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {branchData.map((branch, index) => (
                            <tr key={index}>
                              <td>{branch.branch_name}</td>
                              <td>{branch.branch_location}</td>
                              <td>{branch.branch_type}</td>
                              <td>
                                <span className={`badge bg-${branch.available_online ? 'success' : 'warning'}`}>
                                  {branch.available_online ? 'Online' : 'Offline'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={4}>
                <Card className="shadow-sm">
                  <Card.Header style={{ backgroundColor: "#FFD700", color: "#000" }}>
                    Product Distribution
                  </Card.Header>
                  <Card.Body>
                    <Pie data={pieData} options={{ maintainAspectRatio: false }} height={200} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col>
                <Card className="shadow-sm">
                  <Card.Header style={{ backgroundColor: "#FFD700", color: "#000" }}>
                    Company Details
                  </Card.Header>
                  <Card.Body>
                    <Row className="text-start">
                      <Col md={6}>
                        <p><strong>Registration Number:</strong> {companyData?.registration_number}</p>
                        <p><strong>Industry:</strong> {companyData?.industry}</p>
                        <p><strong>Trading Name:</strong> {companyData?.trading_name}</p>
                        <p><strong>VAT Number:</strong> {companyData?.vat_number}</p>
                      </Col>
                      <Col md={6}>
                        <p><strong>Email:</strong> {companyData?.email1}</p>
                        <p><strong>Phone:</strong> {companyData?.phone_number1}</p>
                        <p><strong>Address:</strong> {companyData?.address}</p>
                        <p><strong>Website:</strong> {companyData?.website}</p>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </motion.div>
        )}
      </div>
    </XGolifeDashboardComponent>
  );
};

export default XGOLifeDashboard;